import { t } from '@lingui/macro';

import OverflowTextModal from '@rover/components/src/OverflowTextModal/OverflowTextModal';
import { ServiceType } from '@rover/constants/src/translations/service.constants';
import { Box, Heading } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';
import type { Pet } from '@rover/rsdk/src/apiClient/latest/model/pet';
import { UnitEnum } from '@rover/rsdk/src/apiClient/latest/model/unitEnum';
import { useCurrentCountryConfiguration } from '@rover/rsdk/src/modules/Data/Core/CountryConfiguration';

import { useRevampProfileContext } from '../../hooks/useRevampProfileContext';
import { sendSectionInteractionEvent } from '../../utils/sendSectionInteractionEvent';
import CatCareExperienceContainer from '../CatCareExperienceContainer/CatCareExperienceContainer';

import RevampQualifications from './RevampQualifications';
import RevampSafetyTrustEnvironment from './RevampSafetyTrustEnvironment';
import RevampSitterPets from './RevampSitterPets';

const HOSTING_SERVICE_SLUGS = new Set([ServiceType.OVERNIGHT_BOARDING, ServiceType.DOGGY_DAY_CARE]);

const RevampPetCareExperienceSection = (): JSX.Element | null => {
  const theme = useTheme();
  const { sitterData, viewMode, serviceType } = useRevampProfileContext();
  const currentCountryConfiguration = useCurrentCountryConfiguration();
  const weightLabel = (currentCountryConfiguration.data?.weightUnit as UnitEnum) || UnitEnum.lbs;

  const catProfile = sitterData.catProviderProfile;

  // Determine if the selected service is a traveling service
  const isTravelingService = (() => {
    if (!serviceType) return false;
    const selectedService = sitterData.services.find(
      (s) => s.serviceType.slug === serviceType || s.slug === serviceType
    );
    if (!selectedService) return false;
    return !HOSTING_SERVICE_SLUGS.has(selectedService.serviceType.slug as ServiceType);
  })();

  // The sitter's short tagline comes from their first service title (de-emphasized in the revamp)
  const sitterHeadline = sitterData.services[0]?.title;

  // Determine experience headline and text based on pet type
  const getExperienceContent = (): { headline: string; text: string } | null => {
    if (viewMode.petType === 'cat-only' && catProfile) {
      return {
        headline: catProfile.headline,
        text: catProfile.careExperience,
      };
    }
    // mixed: show dog experience with service tagline as headline
    return sitterData.experience
      ? {
          headline: sitterHeadline || '',
          text: sitterData.experience,
        }
      : null;
  };

  const experienceContent = getExperienceContent();
  const showCatCareContainer = viewMode.petType === 'mixed' && !!catProfile?.careExperience;

  return (
    <Box
      data-testid="revamp-pet-care-experience"
      width="100%"
      sx={{
        borderBottom: '1px solid',
        borderColor: theme.colors.separator.primary,
        pb: '8x',
        pt: '6x',
      }}
    >
      {experienceContent && (
        <Box pb={showCatCareContainer ? '4x' : '10x'}>
          <OverflowTextModal
            text={experienceContent.text}
            fixedSizedContent={
              <>
                <Heading size="400" mb="6x" webProps={{ responsive: false }}>
                  {t`Pet care experience`}
                </Heading>
                {experienceContent.headline && (
                  <Heading size="200" mb="4x" webProps={{ as: 'h3' }}>
                    {experienceContent.headline}
                  </Heading>
                )}
              </>
            }
            onReadMore={() =>
              sendSectionInteractionEvent('care-experience', { pageVariant: 'revamp' })
            }
          />
        </Box>
      )}

      {/* For mixed pet type, show the cat care experience card with avatar and modal */}
      {showCatCareContainer && (
        <Box pb="10x">
          <CatCareExperienceContainer sitterInfo={sitterData} pageVariant="revamp" />
        </Box>
      )}

      {/* Qualifications */}
      <RevampQualifications
        info={sitterData}
        viewMode={viewMode}
        onReadMore={() => sendSectionInteractionEvent('qualifications', { pageVariant: 'revamp' })}
      />

      {/* Traveling service only: sitter's pets */}
      {isTravelingService && (
        <RevampSitterPets
          pets={sitterData.pets}
          weightLabel={weightLabel}
          onPetPressed={(pet: Pet) => {
            if (process.env.JS_ENV_CLIENT && pet.webUrl) {
              window.open(pet.webUrl, '_blank');
            }
          }}
        />
      )}

      {/* Traveling service only: safety, trust & environment */}
      {isTravelingService && (
        <RevampSafetyTrustEnvironment
          environment={sitterData.environment}
          onReadMore={() => sendSectionInteractionEvent('environment', { pageVariant: 'revamp' })}
        />
      )}
    </Box>
  );
};

export default RevampPetCareExperienceSection;
